/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2014, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../dataSources/utils/DatasourceUtil',
	'dashboard-analytics/util/DashboardFormatter'
], function (
	Class,
	DatasourceUtil,
	Formatter
) {
	'use strict';

	/**
	 * INTENT: The VisAPI is the topmost API to operations that have to do with mappings, slots, or data
	 * (the mapping AND data portions of the spec).
	 */
	const VisAPI = Class.extend({

		// constructor
		init: function (args) {
			VisAPI.inherited('init', this, arguments);
			this._intialize(args);
			this.flag = 'VisAPI';
		},

		destroy: function () {
			this._clear();
			if (this.visModelManager) {
				this.visModelManager.remove();
				this.visModelManager = null;
			}
			VisAPI.inherited('destroy', this, arguments);
		},

		reIntialize (options) {
			this._clear();
			if (this.visRenderSupport) {
				this.visRenderSupport.clear();
				this.visRenderSupport = null;
			}
			this._intialize(options);
		},

		_clear () {
			if (this.visFilterSupport) {
				this.visFilterSupport = null;
			}
			if (this.visPropertySupport) {
				this.visPropertySupport.remove();
				this.visPropertySupport = null;
			}
			if (this.visModelManagerLEFTOVER) {
				this.visModelManagerLEFTOVER = null;
			}
		},

		_intialize (args) {
			const visModelManager = this.visModelManager = args.visModelManager;
			const visRenderSupport = this.visRenderSupport = args.visRenderSupport;
			const visFilterSupport = this.visFilterSupport = args.visFilterSupport;
			const visPropertySupport = this.visPropertySupport = args.visPropertySupport;
			const ownerWidget = args.ownerWidget;
			const visModelManagerLEFTOVER = this.visModelManagerLEFTOVER = args.visModelManagerLEFTOVER;

			this.getVisId = visModelManager.getVisId.bind(visModelManager);
			this.getSuppressViewAnimations = visModelManager.getSuppressViewAnimations.bind(visModelManager);
			this.getModule = visModelManager.getModule.bind(visModelManager);
			this.getMetadataColumn = visModelManager.getMetadataColumn.bind(visModelManager);
			this.getMetadataManager = visModelManager.getMetadataManager.bind(visModelManager);
			this.getUsedMetadataColumns = visModelManager.getUsedMetadataColumns.bind(visModelManager);
			this.getInvalidReason = visModelManager.getInvalidReason.bind(visModelManager);
			this.setInvalidReason = visModelManager.setInvalidReason.bind(visModelManager);
			this.clearModelInvalid = visModelManager.clearModelInvalid.bind(visModelManager);
			this.getDefinition = visModelManager.getDefinition.bind(visModelManager);
			this.getTitle = visModelManager.getTitle.bind(visModelManager);
			this.setTitle = visModelManager.setTitle.bind(visModelManager);
			this.isFilterEditable = visModelManager.isFilterEditable.bind(visModelManager);
			this.getShowTitle = visModelManager.getShowTitle.bind(visModelManager);
			this.supportsSortAction = visModelManager.supportsSortAction.bind(visModelManager);
			this.supportsFormatAction = visModelManager.supportsFormatAction.bind(visModelManager);
			this.supportsBinAction = visModelManager.supportsBinAction.bind(visModelManager);
			this.supportsCustomGroupAction = visModelManager.supportsCustomGroupAction.bind(visModelManager);
			this.supportsMaintainAxisScale = visModelManager.supportsMaintainAxisScale.bind(visModelManager);
			this.getListenForPropChangesFromDefinition = visModelManager.getListenForPropChangesFromDefinition.bind(visModelManager);

			this.getFormatter = function() { return Formatter; };

			// Vis Interactivity settings
			this.getInteractivitySettings = ownerWidget.getInteractivitySettings.bind(ownerWidget);

			this.isOptimizeForSize = ownerWidget.isOptimizeForSize.bind(ownerWidget);

			//SLOTS AND MAPPINGS =======================================
			this.swapSlots = visModelManager.swapSlots.bind(visModelManager);

			this.getDataSlots = visModelManager.getDataSlots.bind(visModelManager);
			this.getLayers = visModelManager.getLayers && visModelManager.getLayers.bind(visModelManager);
			this.getDefaultLayer = visModelManager.getDefaultLayer && visModelManager.getDefaultLayer.bind(visModelManager);
			this.getDataSlotById = visModelManager.getDataSlotById.bind(visModelManager);
			this.getDataItemSlotAndIndex = visModelManager.getDataItemSlotAndIndex.bind(visModelManager);
			this.filterSlotDataItems = visModelManager.filterSlotDataItems.bind(visModelManager);
			this.isMappingEmpty = visModelManager.isMappingEmpty.bind(visModelManager);
			this.isDataItemMapped = visModelManager.isDataItemMapped.bind(visModelManager);
			this.hasBinnedDataItems = visModelManager.hasBinnedDataItems.bind(visModelManager);
			this.isIgnoreDefaultSlotSort = visModelManager.isIgnoreDefaultSlotSort.bind(visModelManager);

			// TODO Old QUERY DEPENDENCIES.... Got some issues when trying to remove. Need to remove refs in vis-summarizer etc.
			this.getQueryAPI = () => null;

			//ANNOTATIONS SUPPORT	
			this.getAnnotations = visModelManager.getAnnotations.bind(visModelManager);
			this.setAnnotations = visModelManager.setAnnotations.bind(visModelManager);

			//POSSIBLE KEY DRIVER SUPPORT
			this.getPossibleKeyDrivers = visModelManager.getPossibleKeyDrivers.bind(visModelManager);
			this.setPossibleKeyDrivers = visModelManager.setPossibleKeyDrivers.bind(visModelManager);

			//RENDER SUPPORT API's ==============================================================
			this.getRenderer = visRenderSupport.getRenderer.bind(visRenderSupport);
			this.getRenderSequence = visRenderSupport.getRenderSequence.bind(visRenderSupport);
			this.getPreferredSize = visRenderSupport.getPreferredSize.bind(visRenderSupport);
			this.renderComplete = visRenderSupport.renderComplete.bind(visRenderSupport);
			this.renderCompleteBeforeAnimation = visRenderSupport.renderCompleteBeforeAnimation.bind(visRenderSupport);
			this.resize = visRenderSupport.resize.bind(visRenderSupport); //function(newWidth, newHeight)
			this.reRender = visRenderSupport.reRender.bind(visRenderSupport); //function(extraInfo)
			this.mapVizRenderForPrint = visRenderSupport.mapVizRenderForPrint.bind(visRenderSupport);

			//FILTER SUPPORT API's =================================================
			this.selectData = visFilterSupport.select.bind(visFilterSupport);
			this.supportsFilterOnColumn = visFilterSupport.supportsFilterOnColumn.bind(visFilterSupport);
			this.getPendingFilters = visFilterSupport.getPendingFilters.bind(visFilterSupport);
			this.getActiveFilters = visFilterSupport.getActiveFilters.bind(visFilterSupport);
			this.getPageContextAPI = visFilterSupport.getPageContextAPI.bind(visFilterSupport);
			this.getFilterInfo = visFilterSupport.getFilterInfo.bind(visFilterSupport);
			this.setSynchronizeDataFilters = visFilterSupport.setSynchronizeDataFilters.bind(visFilterSupport);
			this.setPendingFilters = visFilterSupport.setPendingFilters.bind(visFilterSupport);
			this.getLocalFiltersList = visFilterSupport.getLocalFiltersList.bind(visFilterSupport);
			this.validateFilters = visFilterSupport.validateFilters.bind(visFilterSupport);
			this.cleanUpMissingFilters = visFilterSupport.cleanUpMissingFilters.bind(visFilterSupport);
			this.getAllFiltersAsLocalFiltersForPinning = visFilterSupport.getAllFiltersAsLocalFiltersForPinning.bind(visFilterSupport);
			this.getLocalFilterAndTopBottomInfo = visFilterSupport.getLocalFilterAndTopBottomInfo.bind(visFilterSupport);
			this.clearSelectionsByDataItemIds = visFilterSupport.clearSelectionsByDataItemIds.bind(visFilterSupport);

			//showby
			this.getSelectionAsLocalFilters = visFilterSupport.getSelectionAsLocalFilters.bind(visFilterSupport);
			this.clearSelectionsUsedForShowBy= visFilterSupport.clearSelectionsUsedForShowBy.bind(visFilterSupport);


			//DRILL - TOPN
			this.getDrillInfo = visFilterSupport.getDrillInfo.bind(visFilterSupport);
			this.getTopBottomInfo = visFilterSupport.getTopBottomInfo.bind(visFilterSupport);

			//PROPERTY SUPPORT ==========================================
			this.getPropertyValue = visPropertySupport.getPropertyValue.bind(visPropertySupport);
			this.getPropertyById = visPropertySupport.getPropertyById.bind(visPropertySupport);
			this.getProperties = visPropertySupport.getProperties.bind(visPropertySupport);
			this.getVisPropertiesCSSProxy = visPropertySupport.getVisPropertiesCSSProxy.bind(visPropertySupport);

			//VizDef METADATA SUPPORT ==========================================
			this.getMetaDataValue = visModelManager.getMetaDataValue.bind(visModelManager);

			//WIDGET-TO-WIDGET FILTER apis....
			this.getLocalFilters = visModelManager.getLocalFilters.bind(visModelManager);

			//EVENT apis
			this.onChangeTheme = visRenderSupport.onChangeTheme.bind(visRenderSupport);

			this.clearError = ownerWidget.clearError.bind(ownerWidget);
			this.getScope = ownerWidget.getScope.bind(ownerWidget);
			this.getEventGroupId = ownerWidget.getEventGroupId.bind(ownerWidget);
			this.getWidgetId = ownerWidget.getId.bind(ownerWidget);
			this.getDecoratorAPI = ownerWidget.getDecoratorAPI.bind(ownerWidget);
			this.getDecoratorAPIs = ownerWidget.getDecoratorAPIs.bind(ownerWidget);

			this.on = visModelManager.on.bind(visModelManager);
			this.off = visModelManager.off.bind(visModelManager);

			//Custom Data apis
			this.getCustomDataDecoration = visModelManagerLEFTOVER.getCustomDataDecoration.bind(visModelManagerLEFTOVER);
			this.setCustomDataDecoration = visModelManagerLEFTOVER.setCustomDataDecoration.bind(visModelManagerLEFTOVER);
			this.getDecoratedCustomData = visModelManagerLEFTOVER.getDecoratedCustomData.bind(visModelManagerLEFTOVER);
			this.getCustomData = visModelManagerLEFTOVER.getCustomData.bind(visModelManagerLEFTOVER);

			this.getPromptSpec = ownerWidget.getPromptSpec.bind(ownerWidget);
			this.getFredIsRed = ownerWidget.getFredIsRed.bind(ownerWidget);

			// thumbnail generation
			this.generateThumbnail = ownerWidget.generateThumbnail.bind(ownerWidget);

		},

		/**
		 * TODO: CLEANUP AREA - This is a 'transitional function' to allow us to move to a visAPI rather then pass around a visModel.
		 * 		 The VisAPI should NOT have any data members but the current renderer addresses these members directly.
		 * 		 When we copy the renderer, these direct references to members should be the first thing to go.
		 */
		setDataMembersTEMPORARILY: function (options) {
			this.ownerWidget = options.ownerWidget;
			this.pendingFilters = options.pendingFilters;
			this.localFilters = options.localFilters;
		},

		renderComplete() {
			throw new Error('VisAPI:renderComplete needs to be implemented');
		}
	});

	return VisAPI;
});
